import { alpha, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

import { darkColors, lightColors } from "@/src/theme/tokens";

/**
 * Returns true when the active MUI theme is in dark mode.
 *
 * Use inside `sx` callbacks and style-builder functions instead of duplicating
 * the `muiTheme.palette.mode === "dark"` check everywhere.
 *
 * @example
 * const mySx: SxProps<Theme> = (muiTheme) => ({
 *   color: isDarkMode(muiTheme)
 *     ? muiTheme.palette.text.primary
 *     : muiTheme.palette.secondary.main,
 * });
 */
export const isDarkMode = (muiTheme: Theme): boolean =>
  muiTheme.palette.mode === "dark";

/**
 * Filter that settles photographic content onto the dark surfaces.
 *
 * Photos and vehicle renders carry their light-mode white points, which glare
 * against the dark backgrounds. Trimming contrast to 90% softens them without
 * losing detail. Every `img` element picks this up from the `MuiCssBaseline`
 * override in `theme/customizations/components.ts`; surfaces that paint their
 * artwork through `background-image` have to apply it themselves.
 */
export const DARK_MODE_IMAGE_FILTER = "contrast(90%)";

/**
 * Label color for content sitting on the Primary/Main surface.
 *
 * Dark mode `primary.contrastText` is black, which only reaches ~3.3:1 on the
 * warm dark brand red, so dark mode reads Text/Primary instead while light
 * mode keeps the white Primary/Contrast token.
 */
export const onPrimarySurfaceColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.text.primary
    : muiTheme.palette.semantic.primary.contrastText;

/**
 * Disabled label color for content sitting on the Primary/Main surface.
 *
 * The `action.disabled` tokens are neutral black/white overlays meant for the
 * page backgrounds, so on the brand red they read as grime rather than a muted
 * label - and `action.disabledBackground` at 12% is invisible. Fading the
 * on-surface label instead keeps the disabled state in the same hue family and
 * still clears 3:1 against Primary/Main in both modes (~3.2:1 light,
 * ~3.5:1 dark).
 */
export const disabledOnPrimarySurfaceColor = (muiTheme: Theme): string =>
  alpha(onPrimarySurfaceColor(muiTheme), 0.7);

/**
 * Shared surface styles for contained primary CTAs.
 *
 * Resolves `primary.main` from the active color scheme (#B22D00 in dark mode)
 * and applies the correct on-surface label color for each mode.
 */
export const primaryContainedButtonSx = (
  muiTheme: Theme
): SystemStyleObject<Theme> => ({
  backgroundColor: muiTheme.palette.primary.main,
  color: onPrimarySurfaceColor(muiTheme),
  "&:hover": {
    backgroundColor: muiTheme.palette.primary.dark,
    color: onPrimarySurfaceColor(muiTheme)
  }
});

/**
 * Brand accent color for text and icons sitting on a page background.
 *
 * Dark mode Primary/Main is a deep burnt red that only reaches ~2.9:1 against
 * the dark page background, so accent text reads Primary/Light instead.
 */
export const primaryAccentColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.semantic.primary.light
    : muiTheme.palette.semantic.primary.main;

/**
 * Label color for the selected tab.
 *
 * Even Primary/Light is a muted burnt red against the dark page background, so
 * dark mode reads Text/Primary ink for the strongest selected/unselected
 * contrast and leaves the brand accent to the indicator underline.
 */
export const selectedTabLabelColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.text.primary
    : muiTheme.palette.semantic.primary.main;

/**
 * Ink for section headings and the accent rule underneath them.
 *
 * Both burnt reds stay muted against the dark page background, so dark mode
 * reads Text/Primary for the heading and its underline while light mode keeps
 * the brand accent.
 */
export const sectionHeadingAccentColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.text.primary
    : muiTheme.palette.semantic.primary.main;

/**
 * Dark mode ink for links and clickable breadcrumbs.
 *
 * Dark mode Primary/Main is a burnt red that only reaches ~2.6:1 against the
 * dark surfaces, so links either disappeared or fell back to Text/Primary and
 * became indistinguishable from the copy around them. Misc/Dark Mode Link is
 * the warm orange the design system carries for exactly this, and clears 4.5:1
 * on every dark surface the app paints.
 *
 * Callers are expected to guard on {@link isDarkMode} themselves; light mode
 * links keep whatever brand color their surface already uses.
 */
export const darkModeLinkColor = (muiTheme: Theme): string =>
  muiTheme.palette.misc.link ?? muiTheme.palette.primary.light;

/** Dark mode ink for a link the reader has already followed. */
export const darkModeVisitedLinkColor = (muiTheme: Theme): string =>
  muiTheme.palette.misc.darkVisitedLink ?? darkModeLinkColor(muiTheme);

/**
 * Ink for a link the reader has already followed.
 *
 * Light mode reads Other/Visited Link (`#551A8B`); dark mode reads Other/Misc
 * Visited Link. Breadcrumbs and other navigation chrome that always point at
 * a page the reader just left should not use this.
 */
export const visitedLinkColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? darkModeVisitedLinkColor(muiTheme)
    : (muiTheme.palette.misc.visitedLink ?? "#551A8B");

/** Followed-state ink for a real `<a>`, in both color modes. */
export const visitedLinkSx = (muiTheme: Theme): SystemStyleObject<Theme> => ({
  "&:visited": {
    color: visitedLinkColor(muiTheme)
  }
});

/**
 * Dark mode ink for a text link, including its followed state.
 *
 * Spread over a link's own colors, after them, so light mode is left untouched.
 * Navigation chrome that repeats links the reader has necessarily already been
 * through - breadcrumbs above all - takes {@link darkModeLinkColor} on its own
 * instead, so it never renders in the followed color.
 */
export const darkModeTextLinkSx = (
  muiTheme: Theme
): SystemStyleObject<Theme> =>
  isDarkMode(muiTheme)
    ? {
        color: darkModeLinkColor(muiTheme),
        ...visitedLinkSx(muiTheme)
      }
    : {};

/**
 * Label color for content sitting on the Snackbar/Tooltip surface.
 *
 * `misc.snackbarBackground` is an inverted surface: near-black in light mode
 * and near-white (`#E2E2E2`) in dark mode. MUI's default white tooltip label
 * therefore disappears in dark mode, which reads Text/Primary ink instead.
 */
export const onSnackbarSurfaceColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? alpha(muiTheme.palette.common.black, 0.87)
    : muiTheme.palette.common.white;

/**
 * Snackbar/Tooltip surface with the two modes swapped.
 *
 * `misc.snackbarBackground` inverts per mode, which suits a floating snackbar
 * but not a chip pinned onto a photo: the near-white dark mode fill reads as a
 * bright block on the dark card. Surfaces that want the opposite pairing (a
 * dark fill in dark mode, a light fill in light mode) take these instead.
 */
export const invertedSnackbarSurfaceColor = (muiTheme: Theme): string => {
  const swapped = isDarkMode(muiTheme)
    ? lightColors.misc.snackbarBackground
    : darkColors.misc.snackbarBackground;

  return swapped ?? muiTheme.palette.misc.snackbarBackground;
};

/** Label color for content on {@link invertedSnackbarSurfaceColor}. */
export const onInvertedSnackbarSurfaceColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.common.white
    : alpha(muiTheme.palette.common.black, 0.87);

/**
 * Elevation for image and upload surfaces that must stay visible in both modes.
 *
 * MUI drop shadows are black, so they disappear against the dark backgrounds.
 * Dark mode uses a white outline plus glow to keep card edges readable while
 * light mode keeps the standard elevation.
 */
export const elevatedSurfaceShadow = (muiTheme: Theme): string => {
  if (!isDarkMode(muiTheme)) {
    return muiTheme.shadows[4];
  }

  const outline = alpha(muiTheme.palette.common.white, 0.16);
  const glow = alpha(muiTheme.palette.common.white, 0.24);

  return `0 0 0 1px ${outline}, 0 4px 16px 0 ${glow}`;
};

/**
 * Surface styles for Paper-based panels that sit flush on the page background.
 *
 * Light mode Background/Paper and Background/Default are both white, so these
 * panels read as part of the page. Dark mode Paper is lighter than Default and
 * would otherwise split the page into two tones.
 */
export const flushPageSurfaceSx: SystemStyleObject<Theme> = {
  backgroundColor: "background.default",
  backgroundImage: "none"
};

/**
 * Distinct strip fill that stays visible against Background/Paper.
 *
 * Dark mode Misc/Timeline Background falls back to paper, so strips use an
 * elevation surface instead (same rule as the consumer header dropdown pills).
 */
export const distinctFillColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.misc.elevation2
    : muiTheme.palette.misc.timelineBackground;

/**
 * Fill and ink for the unread notification count badge.
 *
 * `Badge color="secondary"` is near-white in dark mode and glares on the
 * header, so dark mode reads the brand Primary/Main fill with a white count.
 * Every breakpoint shares these so the count keeps one color as the header
 * collapses from the desktop bell into the mobile hamburger.
 */
export const unreadCountBadgeColors = (
  muiTheme: Theme
): SystemStyleObject<Theme> => {
  const darkMode = isDarkMode(muiTheme);

  return {
    backgroundColor: darkMode
      ? muiTheme.palette.primary.main
      : muiTheme.palette.secondary.main,
    color: darkMode
      ? muiTheme.palette.common.white
      : muiTheme.palette.secondary.contrastText
  };
};

interface InfoBadgeColors {
  backgroundColor: string;
  color: string;
}

/**
 * Fill and ink for Info-toned badges and chips.
 *
 * Dark mode Info/Main is a pale blue whose black contrast text reads as a
 * different chip from the light-mode one, so dark mode keeps the light
 * Info/Main fill and reads Text/Primary for the label.
 */
export const infoBadgeColors = (muiTheme: Theme): InfoBadgeColors => {
  const darkMode = isDarkMode(muiTheme);

  return {
    backgroundColor: darkMode
      ? lightColors.info.main
      : muiTheme.palette.info.main,
    color: darkMode
      ? muiTheme.palette.text.primary
      : muiTheme.palette.info.contrastText
  };
};

/**
 * Filled background for informational panels that sit on the page background.
 *
 * Dark mode Misc/Cold Background is a warm near-red brown that reads as an
 * error surface when used as a large fill, so filled panels take a neutral
 * elevation surface instead and leave the cold token to hairline borders.
 */
export const coldPanelFillColor = (muiTheme: Theme): string =>
  isDarkMode(muiTheme)
    ? muiTheme.palette.misc.elevation3
    : muiTheme.palette.misc.coldBackground;
